import React, { useCallback, useMemo, useRef, useState } from "react";
// ANT DESIGN COMPONENTS
import { Tooltip } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
// CUSTOM HOOKS
import useGetAllMessages from "../../../../customHooks/GET_requests/useGetAllMessages";
// COMPONENTS
import ActionTypeTag from "./ActionTypeTag";
import ActionDetails, { ActionCommandSummary } from "./ActionDetails";
import ActionControls from "./ActionControls";
// FIRING MODE
import { useFiresOnFloorClick } from "../../../../customHooks/useActionFiringMode";
import { FiringSurface } from "../../../../utils/actionFiringMode";
import ActionFloorStrip from "./ActionFloorStrip";
import ActionOutputNumberInput from "./ActionOutputNumberInput";
import ActionSequenceControls from "./ActionSequenceControls";
import { sequenceForAction } from "../actionSequenceConfig";
// INTERFACES
import { BankActionInterface, BankFloorInterface } from "../../../../interfaces/Bank.interface";
// UTILS
import {
  resolveCommandDisplayName,
  resolveCommandEnum,
  resolveMessageDisplayNames,
  UNBOUND_ACTION_REASON,
} from "../../actionButtons/actionButtonUtils";
import { NoFloorAvailableContext } from "../../actionButtons/useAwaitingTargetReason";
import { controlRequiresFloor, takesOutputNumber } from "../../../../utils/controlFloorRules";
// CONFIG
import { ActionButtonTypeName, ActionTypeConfig } from "../actionTypeConfig";
// UUID
import formatUuidT from "../../../../flatbuffers/display/uuid";

/** What a row's buttons can be aimed at, and what the operator has picked. */
interface RowTargetState {
  takesOutput: boolean;
  selectedOutput: number | null;
  takesFloor: boolean;
  selectedFloorIndex: number | null;
  /** The command's position 0 is a real floor (ShuttleFloor). */
  requiresFloor: boolean;
}

/**
 * The position a row's buttons dispatch to, in the three-state shape
 * `dispatchFloor` reads: the picked floor for a floor-based row, the entered
 * output number for a GenericOutput row (`null` while either is still missing,
 * which blocks the buttons), and `undefined` for a row that takes neither.
 *
 * A row whose command requires a floor but has none to pick is blocked too,
 * rather than given the `undefined` that would dispatch at 0 — for ShuttleFloor
 * 0 is a floor, so that press would change a floor nobody chose.
 */
function rowTarget(row: RowTargetState): number | null | undefined {
  if (row.takesOutput) return row.selectedOutput;
  if (row.takesFloor) return row.selectedFloorIndex;
  if (row.requiresFloor) return null;
  return undefined;
}

/**
 * One row of the Actions list: accent icon, operator label, type tag and the
 * type's command controls, over a collapsible body holding the derived metadata
 * and the per-command breakdown.
 *
 * The card is pure presentation. Every command, timer, valid-state check and
 * cleanup still lives in the behaviour component that <ActionControls> mounts,
 * so a row that used to send X on click still sends exactly X.
 *
 * `color` / `toggleColor` are treated as visual configuration only — they tint
 * the command buttons, and are never read for meaning (red is not "off") nor
 * used as the card surface, which stays neutral so a screen full of actions
 * does not become a wall of red and green.
 *
 * The left edge, the type tag and the icon all carry the TYPE's accent, which
 * the operator does not control: scanning the list tells you which rows are
 * toggles, which are hold-to-press, which switch, without reading a label.
 */
export default function ActionCard({
  action,
  typeName,
  config,
  carService,
  theme,
  disableTurnBack,
  setDisableTurnBack,
  floors = [],
}: {
  action: BankActionInterface;
  typeName: ActionButtonTypeName;
  config: ActionTypeConfig;
  carService: number;
  theme: string;
  disableTurnBack: boolean;
  setDisableTurnBack: (newValue: boolean) => void;
  /**
   * The floors this row's command may be sent to, already filtered for door
   * side, direction, the lift's own served floors and the operator's per-floor
   * ACTION scope, and ordered top-down by the caller.
   *
   * EMPTY is the signal that this row takes no floor — the caller only passes
   * floors for a command `actionTakesFloor` classifies as positional. A row
   * that takes a floor but has none available also lands here, and deliberately
   * behaves exactly as it does today rather than becoming a dead row — except
   * ShuttleFloor, whose 0 is a floor, so it is blocked instead: see the
   * `targetFloor` note below.
   */
  floors?: BankFloorInterface[];
}) {
  const [t] = useTranslation("global");
  const { messages } = useGetAllMessages();

  // Collapsed by default: the list is a control surface first, and an operator
  // scanning for the button to press should not have to scroll past six
  // paragraphs of documentation to reach the sixth row.
  const [expanded, setExpanded] = useState(false);
  const detailsId = `actionCard__details-${action.id}`;

  // Which floor this row's commands are aimed at. Nothing is preselected: the
  // whole defect being fixed is a positional command going out at a floor the
  // operator never chose, and quietly choosing one FOR them is the same defect
  // with a nicer face on it.
  const [selectedFloorIndex, setSelectedFloorIndex] = useState<number | null>(null);

  // The output number a GenericOutput row is aimed at, chosen the same way: by
  // the operator, never preset.
  const [selectedOutput, setSelectedOutput] = useState<number | null>(null);

  /**
   * Whether picking a floor should also SEND, for this deployment.
   *
   * Only half the answer. ActionControls holds the other half — whether this
   * row's button TYPE can be collapsed to a single act at all — and withholds
   * the registration below when it cannot. So a HOLDBACK row on a ONE_CLICK
   * deployment still needs its hold: the operator's preference is about
   * removing a redundant step, not about waiving a safeguard.
   */
  const oneClick = useFiresOnFloorClick(FiringSurface.ActionCard);

  /**
   * The behaviour component's send, if it registered one.
   *
   * A ref rather than state on purpose. Storing it in state would re-render
   * this card on registration, re-rendering the button, which registers again
   * — and in StrictMode's double-invoked development effects that loop ends in
   * a duplicate elevator command. A ref registers silently.
   */
  const registeredSend = useRef<((floorOverride?: number) => void) | null>(null);

  const registerSend = useCallback((send: (floorOverride?: number) => void) => {
    registeredSend.current = send;
  }, []);

  /**
   * A floor was picked.
   *
   * The floor is passed INTO the send rather than left for it to read back off
   * `targetFloor`, because `targetFloor` derives from the state this very call
   * is setting: React has not applied it yet, so the button would dispatch at
   * the PREVIOUSLY selected floor. That is a command sent to a floor the
   * operator did not choose — the exact failure the two-step interaction exists
   * to prevent, reintroduced by the shortcut meant to be a convenience.
   */
  const handleSelectFloor = useCallback(
    (floorIndex: number) => {
      setSelectedFloorIndex(floorIndex);
      if (oneClick) registeredSend.current?.(floorIndex);
    },
    [oneClick],
  );

  /**
   * The multi-step sequence this row runs, or undefined for the ordinary
   * single-dispatch rows. Resolved from the row's message, so a bank that
   * configures VIP gets the stepped controls with nothing extra to set up.
   */
  const sequence = useMemo(() => sequenceForAction(action, messages), [action, messages]);

  // Display-only resolution. The same lookups happen inside the behaviour
  // component for dispatch; duplicating them here keeps the card from having to
  // reach into that component's state, and the two cannot disagree because both
  // resolve through the same helper — including the same fallback to the row's
  // own `command` option, so a card never names a command the button below it
  // refuses to send, or vice versa.
  const primaryCommandName = useMemo(
    () => resolveCommandDisplayName(messages, action.messageId, action.command),
    [messages, action.messageId, action.command],
  );

  /**
   * The enum this row's primary control would actually put on the wire, or null
   * when nothing resolves.
   *
   * Resolved through `resolveCommandEnum` — the same helper the button below
   * calls to decide what to send — rather than by reading `action.cmd`. `cmd` is
   * one of several inputs the normaliser folds into `messageId`, and rows from
   * the older path carry no `cmd` at all while resolving perfectly well; keying
   * off it would mark every one of those unbound.
   */
  const primaryCommandEnum = useMemo(
    () => resolveCommandEnum(messages, action.messageId, action.command),
    [messages, action.messageId, action.command],
  );

  // A GenericOutput row names an output number, never a floor. The caller
  // already passes it no floors; testing it here as well means a floor list
  // handed in by mistake still cannot turn the output number into a floor.
  const takesOutput = takesOutputNumber(primaryCommandEnum);
  const takesFloor = floors.length > 0 && !takesOutput;
  const requiresFloor = controlRequiresFloor(primaryCommandEnum);

  /**
   * The position handed to the behaviour component, in the three-state shape
   * `dispatchFloor` reads.
   *
   * `undefined` when this row has no strip and no output field — either the
   * command takes no position, or it takes a floor but no floor is available to
   * pick. Both cases dispatch at 0 exactly as they do today: a row whose bank
   * has not loaded its floors must not turn into a permanently disabled row, and
   * the strip is the only thing that could ever un-disable it. A GenericOutput
   * row always has its field, so it is never in that case. A ShuttleFloor row
   * is the exception: its 0 is a real floor, so with no floor to pick it is
   * blocked, and says so below, instead of changing a floor nobody chose.
   */
  const targetFloor = rowTarget({
    takesOutput,
    selectedOutput,
    takesFloor,
    selectedFloorIndex,
    requiresFloor,
  });

  const toggleCommandName = useMemo(
    () =>
      resolveCommandDisplayName(
        messages,
        action.toggleMessageId,
        action.toggleCommand ?? action.toggle_command,
      ),
    [messages, action.toggleMessageId, action.toggleCommand, action.toggle_command],
  );

  // Every button type is a two-command action: the paired command is either a
  // second control (TOGGLE, TOGGLETIMER, SWITCH) or the one sent automatically
  // on release / when the timer elapses (HOLDBACK, TURNBACK,
  // ENERGIZER_TURN_BACK). The popover therefore always documents it — a row
  // saved without one is misconfigured, and the empty command line is how the
  // operator sees that, rather than the card silently describing a different
  // shape of action than the one the controls actually offer.
  const secondaryLabel = action.toggleLabel || toggleCommandName;

  // BankActionInterface types these as strings, but the API delivers them as
  // FlatBuffer UuidT values on the binary path — rendering one raw prints
  // "[object Object]". formatUuidT handles both shapes and returns "" for an
  // absent id, which the detail row shows as "--".
  const primarySummary: ActionCommandSummary = {
    label: action.label,
    commandName: primaryCommandName,
    validStateNames: resolveMessageDisplayNames(messages, action.validStates),
    timer: action.timer,
    actionId: formatUuidT(action.actionId),
    messageId: formatUuidT(action.messageId),
  };

  const secondarySummary: ActionCommandSummary = {
    label: action.toggleLabel,
    commandName: toggleCommandName,
    validStateNames: resolveMessageDisplayNames(messages, action.toggleValidStates),
    timer: action.toggleTimer || action.timer,
    actionId: formatUuidT(action.toggleActionId),
    messageId: formatUuidT(action.toggleMessageId),
  };

  /**
   * Short derived fragments (bound command, cooldown, sequence, hint) shown in
   * the expanded body, joined by dots. They stay terse: this is the summary
   * above the per-command breakdown, not a second copy of it.
   */
  const metadata: string[] = [];
  if (primaryCommandName !== "") metadata.push(primaryCommandName);

  if (typeName === "TOGGLETIMER" && action.timer > 0) {
    metadata.push(`${t("monitoringActions.cooldown", "Cooldown")}: ${action.timer}s`);
  } else if ((typeName === "TURNBACK" || typeName === "ENERGIZER_TURN_BACK") && action.timer > 0) {
    metadata.push(`${t("monitoringActions.auto", "Auto")}: ${secondaryLabel || "--"}`);
    metadata.push(`${action.timer}s`);
  } else if (typeName === "SWITCH") {
    metadata.push(
      `${action.label || "--"} → ${action.timer || 1}s → ${action.toggleLabel || "--"}`.trim(),
    );
  } else if (typeName === "HOLDBACK") {
    if (config.hintKey && config.hint) metadata.push(t(config.hintKey, config.hint));
    if (action.timer > 0) metadata.push(`${action.timer}s`);
  }

  const metaEntries = metadata.filter((entry) => entry !== "");

  /**
   * Does this row bind a command the controller will actually accept?
   *
   * Nothing resolves when the action's bound message is absent from the catalog
   * — which now includes the case that matters here: a message whose CATEGORY
   * is not Control. The categories are separate enums that collide numerically,
   * so a non-Control binding used to resolve to whichever Control member shared
   * its number and offer to send that instead; the normaliser refuses those, and
   * they arrive here bound to nothing.
   *
   * Such a row is shown, not hidden. Hiding it would leave a misconfiguration
   * invisible to the person commissioning the bank, and deletion lives in the
   * wizard's Actions table, so the row still has to be findable. What it must
   * NOT do is look pressable.
   *
   * Only the PRIMARY command is tested. A row whose paired command is the
   * missing one can still send its primary, and the button already guards the
   * pair on its own — disabling the whole card there would take away a control
   * that works.
   */
  const unbound = primaryCommandEnum === null;

  // The icon takes the TYPE's accent, never the operator-configured colour:
  // the left edge, the type tag and the icon are three views of one fact, and
  // tinting the icon with `action.color` made them disagree on rows where the
  // operator picked a colour that belongs to a different type.

  return (
    <div
      className={`actionCard actionCard__${theme}${expanded ? " actionCard--expanded" : ""}`}
      style={{ borderLeftColor: config.accent }}
      data-testid="action-card"
      data-action-type={typeName}
    >
      <div className="actionCard__row">
        {/* The summary is a real <button>, not a div with onClick: it is the
            collapse trigger, so it has to be tabbable, announce its expanded
            state, and respond to Enter/Space without hand-rolling any of it.
            It deliberately does NOT wrap the command controls — a click meant
            for a command must never also toggle the card. */}
        <button
          type="button"
          className="actionCard__summary"
          aria-expanded={expanded}
          aria-controls={detailsId}
          onClick={() => setExpanded((open) => !open)}
        >
          <span className="actionCard__icon">
            <FontAwesomeIcon icon={config.icon} style={{ color: config.accent }} />
          </span>

          <span className="actionCard__content">
            <span className="actionCard__titleRow">
              <Tooltip title={action.label} placement="topLeft">
                <span className="actionCard__title">{action.label || "--"}</span>
              </Tooltip>
            </span>
            {/* The type tag sits UNDER the label: on a narrow card an inline tag
                competed with the operator's own name for the same line and
                pushed it into an ellipsis. */}
            <span className="actionCard__tagRow">
              <ActionTypeTag config={config} theme={theme} />
            </span>
          </span>

          <FontAwesomeIcon
            icon={faChevronDown}
            className={`actionCard__chevron${expanded ? " actionCard__chevron--open" : ""}`}
          />
        </button>

        <div className="actionCard__controls">
          {unbound ? (
            <Tooltip title={UNBOUND_ACTION_REASON} placement="topRight">
              <span className="actionCard__unbound" data-testid="action-card-unbound">
                No command
              </span>
            </Tooltip>
          ) : (
            !sequence && (
              // Tells the buttons' disabled tooltip that there is no floor to
              // select, so it does not ask for one.
              <NoFloorAvailableContext.Provider value={requiresFloor && !takesFloor}>
                <ActionControls
                  action={action}
                  typeName={typeName}
                  carService={carService}
                  disableTurnBack={disableTurnBack}
                  setDisableTurnBack={setDisableTurnBack}
                  targetFloor={targetFloor}
                  registerSend={registerSend}
                />
              </NoFloorAvailableContext.Provider>
            )
          )}
        </div>
      </div>

      {/* The floor selector sits UNDER the row, full width, rather than beside
          the command buttons: a bank with twenty floors would otherwise push
          the buttons off the card, and the strip is the step you take BEFORE
          pressing one. It is outside the `__summary` button for the same reason
          the controls are — a click meant for a floor must never also collapse
          the card. */}
      {sequence && !unbound && (
        <ActionSequenceControls
          sequence={sequence}
          command={primaryCommandEnum}
          floors={floors}
          colorAction={action.color}
        />
      )}

      {takesFloor && !sequence && (
        <ActionFloorStrip
          floors={floors}
          selectedFloorIndex={selectedFloorIndex}
          onSelectFloor={handleSelectFloor}
        />
      )}

      {/* Not wired to the one-click send the strip uses: entering "12" passes
          through "1", so a field that sent on change would switch output 1
          first. The row's own button is the sending act. */}
      {takesOutput && !sequence && (
        <ActionOutputNumberInput value={selectedOutput} onChange={setSelectedOutput} />
      )}

      {requiresFloor && !takesFloor && !sequence && (
        <div className="actionCard__floorSelect" data-testid="action-card-no-floor">
          <span className="actionCard__floorSelectLabel">
            {t(
              "monitoringActions.noFloorAvailable",
              "No floor is available to send this command to, so it cannot be sent. The bank's floors may still be loading, or none is in your scope.",
            )}
          </span>
        </div>
      )}

      {/* Unmounted while collapsed rather than hidden with CSS: the body is
          pure text, so there is nothing to preserve across a toggle, and an
          off-screen copy of every row's documentation would still be read out
          by a screen reader walking the list. */}
      {expanded && (
        <div className="actionCard__details" id={detailsId}>
          <ActionDetails
            config={config}
            primary={primarySummary}
            secondary={secondarySummary}
            metadata={metaEntries}
          />
        </div>
      )}
    </div>
  );
}
